'use client';

import type { ReactNode } from 'react';
import type { Household } from 'src/types/household';
import type { TagData } from 'src/types/tag-data';
import type { HouseholdMatchField, HouseholdMatchInfo } from 'src/utils/household/useHouseholdSearch';
import { Text } from '@savvy-kit/foundations/text';
import { isClient } from 'src/utils/advisor-dash/household';
import { convertSnakeCaseToSentenceCase } from 'src/utils/url';

import { formatLargeDollarAmount, formatPhoneNumber, formatTimestampToRelative } from '../../../utils/formatting';
import { HouseholdTagsDisplay } from '../shared/household-tags-display';

const FIELD_LABELS: Record<HouseholdMatchField, string> = {
  HOUSEHOLD_NAME: 'Household',
  CLIENT_NAME: 'Name',
  EMAIL: 'Email',
  PHONE: 'Phone',
  SECONDARY_EMAIL: 'Email',
  SECONDARY_PHONE: 'Phone',
};

export type HouseholdSearchResultProps = {
  readonly household: Household;
  readonly matchInfos?: readonly HouseholdMatchInfo[];
  readonly query?: string;
  readonly tags?: readonly TagData[];
};

// Highlight the matched query substring with a light-green mark; when the query
// is not a literal substring (fuzzy/trigram match) return the text unhighlighted.
function highlight(text: string, query: string | undefined): ReactNode {
  const q = query?.trim();
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  const end = idx + q.length;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded-sm bg-primary-background px-0.5 text-dark">{text.slice(idx, end)}</mark>
      {text.slice(end)}
    </>
  );
}

export function HouseholdSearchResult({ household, matchInfos = [], query, tags = [] }: HouseholdSearchResultProps) {
  const isClientHousehold = isClient(household.householdStage);
  const stage = convertSnakeCaseToSentenceCase(household.householdStage ?? '');

  const householdNameMatch = matchInfos.some((m) => m.field === 'HOUSEHOLD_NAME');
  // Top 2 non-household-name matches, shown as "Label: value" context.
  const contextMatches = matchInfos.filter((m) => m.field !== 'HOUSEHOLD_NAME').slice(0, 2);

  const revenue =
    Number(household.advisorAnnualRevenue) > 0 ? formatLargeDollarAmount(Number(household.advisorAnnualRevenue)) : '–';
  const assets = Number(household.savvyAum) > 0 ? formatLargeDollarAmount(Number(household.savvyAum)) : '–';
  const estAum = household.prospectData?.estimatedAum
    ? formatLargeDollarAmount(Number(household.prospectData.estimatedAum))
    : '–';

  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5">
        <Text variant="subtitle-sm" color="dark" className="truncate" render={<p />}>
          {householdNameMatch ? highlight(household.name, query) : household.name}
        </Text>
        <Text variant="body-sm" className="shrink-0" render={<span />}>
          · {isClientHousehold ? 'Client' : 'Prospect'}
        </Text>
      </div>

      {contextMatches.length > 0 && (
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
          {contextMatches.map((m) => {
            const isPhone = m.field === 'PHONE' || m.field === 'SECONDARY_PHONE';
            const displayValue = isPhone && m.value ? formatPhoneNumber(m.value) : m.value;
            return (
              <span key={`${m.field}-${m.value}`} className="text-[12px] text-secondary">
                {FIELD_LABELS[m.field]}
                {m.value && (
                  <span className="text-secondary">
                    {': '}
                    {isPhone ? displayValue : highlight(displayValue, query)}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs">
        {isClientHousehold ? (
          <>
            <span className="text-secondary">
              Revenue <span className="font-medium text-dark">{revenue}</span>
            </span>
            <span className="text-secondary">
              Assets <span className="font-medium text-dark">{assets}</span>
            </span>
          </>
        ) : (
          <>
            <span className="text-secondary">
              Stage <span className="font-medium text-dark">{stage || '–'}</span>
            </span>
            <span className="text-secondary">
              Est. AUM <span className="font-medium text-dark">{estAum}</span>
            </span>
          </>
        )}
        <span className="text-secondary">
          Last contact{' '}
          <span className="font-medium text-dark">
            {household.lastContactAt ? formatTimestampToRelative(household.lastContactAt) : '–'}
          </span>
        </span>
      </div>

      {tags.length > 0 && (
        <div className="mt-1.5">
          <HouseholdTagsDisplay
            tags={tags}
            hideIndicator
            tagClassName="shrink-0"
            containerClassName="flex flex-wrap gap-1"
          />
        </div>
      )}
    </div>
  );
}
